import { isNil } from "../identity";
import { mapNotNil } from "../arrays";
import { NavigateOptions, useSearchParams } from "react-router-dom";
import { useCallback } from "react";

/**
 * represents an object that can serialize a value to a query string param and parse it back.
 * these can be passed to useQuery for strong typing with query params.
 **/
export type QueryParamConverter<T> = {
  /**
   * @returns the given value represented as a string
   **/
  serialize(value: T): string;

  /**
   * parses a given query string into a value of type T. if parsing fails for some reason,
   * this should return undefined
   **/
  parse(value: string): T | undefined;
};

/**
 * converts an array of values to/from a comma-join string
 **/
export const asArray = <T>(
  valueConverter: QueryParamConverter<T>
): QueryParamConverter<T[]> => ({
  serialize: (vs) => vs.map((v) => valueConverter.serialize(v)).join(","),
  parse: (v) => mapNotNil(v.split(","), (v) => valueConverter.parse(v)),
});

/**
 * converts query params to integers using Number.parseInt.
 **/
export const asInt: QueryParamConverter<number> = {
  serialize: (v) => `${v}`,
  parse: (v) => {
    const n = Number.parseInt(v);
    return Number.isNaN(n) ? undefined : n;
  },
};

/**
 * @returns a QueryParamConverter that only allows the value to be one of the options provided
 **/
export const asOneOf = <T extends string>(
  ...options: T[]
): QueryParamConverter<T> => ({
  serialize: (v) => v,
  parse: (v) => ((options as string[]).includes(v) ? (v as T) : undefined),
});

/**
 * converts query param values to strings (i.e. makes no changes)
 **/
export const asString: QueryParamConverter<string> = {
  serialize: (v) => v,
  parse: (v) => v,
};

/**
 * converts query params to booleans. only the strings "true" and "false" parse;
 * anything else returns undefined.
 **/
export const asBoolean: QueryParamConverter<boolean> = {
  serialize: (v) => (v ? "true" : "false"),
  parse: (v) => {
    if (v === "true") {
      return true;
    }
    if (v === "false") {
      return false;
    }
  },
};

/**
 * Hook to read and write to query string parameters as if they were state variables
 *
 * Supports encoding and parsing a number of query types (string, integer, boolean, string union)
 */
export const useQuery = <T>(
  key: string,
  serde: QueryParamConverter<T>,
  navigateOptions?: NavigateOptions
): [T | undefined, (value: T | undefined) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const result = searchParams.get(key);
  const set = useCallback(
    (value: T | undefined) => {
      const newParams = new URLSearchParams(searchParams);
      if (isNil(value)) {
        newParams.delete(key);
      } else {
        newParams.set(key, serde.serialize(value));
      }
      setSearchParams(newParams, navigateOptions);
    },
    [key, navigateOptions, searchParams, serde, setSearchParams]
  );
  return [isNil(result) ? undefined : serde.parse(result), set];
};
